import React, { useState, useEffect, useCallback } from 'react';
import { useLocalParticipant } from '@livekit/components-react';
import { Track } from 'livekit-client';
import './AudioControls.css';

export const MuteButton = () => {
  const [isMuted, setIsMuted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isConnecting, setIsConnecting] = useState(true);

  // Hook must be called unconditionally at top level
  const { localParticipant } = useLocalParticipant();

  useEffect(() => {
    // Check connection state
    if (localParticipant) {
      setIsConnecting(false);

      const checkMuteState = () => {
        try {
          const micPub = localParticipant.getTrackPublication(Track.Source.Microphone);
          if (micPub) {
            setIsMuted(micPub.isMuted);
          }
        } catch (err) {
          console.error('Error checking mute state:', err);
        }
      };

      checkMuteState();

      const handleTrackMuted = (publication) => {
        if (publication.source === Track.Source.Microphone) {
          setIsMuted(true);
        }
      };

      const handleTrackUnmuted = (publication) => {
        if (publication.source === Track.Source.Microphone) {
          setIsMuted(false);
        }
      };

      localParticipant.on('trackMuted', handleTrackMuted);
      localParticipant.on('trackUnmuted', handleTrackUnmuted);

      return () => {
        localParticipant.off('trackMuted', handleTrackMuted);
        localParticipant.off('trackUnmuted', handleTrackUnmuted);
      };
    } else {
      setIsConnecting(true);
    }
  }, [localParticipant]);

  const toggleMute = async () => {
    if (!localParticipant || isLoading) return;

    setIsLoading(true);
    setError(null);

    try {
      if (isMuted) {
        await localParticipant.setMicrophoneEnabled(true);
        setIsMuted(false);
      } else {
        await localParticipant.setMicrophoneEnabled(false);
        setIsMuted(true);
      }
    } catch (err) {
      console.error('Failed to toggle mute:', err);
      setError('Failed to toggle mute');
    }
    setIsLoading(false);
  };

  if (error) {
    return (
      <button className="mute-btn muted" disabled>
        ⚠️ Mic Error
      </button>
    );
  }

  // Show connecting state while waiting for LiveKit connection
  if (isConnecting) {
    return (
      <button className="mute-btn unmuted" disabled>
        ⏳ Connecting...
      </button>
    );
  }

  return (
    <button
      onClick={toggleMute}
      disabled={isLoading || !localParticipant}
      className={`mute-btn ${isMuted ? 'muted' : 'unmuted'}`}
    >
      {isLoading ? '⏳' : (isMuted ? '🔇 Unmute' : '🎤 Mute')}
    </button>
  );
};

export const AudioOutputToggle = () => {
  const [useSpeaker, setUseSpeaker] = useState(true);
  const [isSupported, setIsSupported] = useState(true);
  const [availableDevices, setAvailableDevices] = useState([]);

  // Check for device support on mount
  useEffect(() => {
    const checkSupport = async () => {
      try {
        // Check if setSinkId is supported
        const testAudio = document.createElement('audio');
        if (typeof testAudio.setSinkId !== 'function') {
          setIsSupported(false);
          return;
        }

        // Get available audio output devices
        const devices = await navigator.mediaDevices.enumerateDevices();
        const audioOutputs = devices.filter(d => d.kind === 'audiooutput');
        setAvailableDevices(audioOutputs);

        // If only one device (or none with labels), limited functionality
        if (audioOutputs.length <= 1) {
          setIsSupported(false);
        }
      } catch (err) {
        console.log('Audio output detection error:', err);
        setIsSupported(false);
      }
    };

    checkSupport();
  }, []);

  const toggleAudioOutput = useCallback(async () => {
    const newUseSpeaker = !useSpeaker;
    setUseSpeaker(newUseSpeaker);

    const audioElements = document.querySelectorAll('audio');

    for (const audio of audioElements) {
      try {
        if (typeof audio.setSinkId === 'function' && availableDevices.length > 1) {
          // Find earpiece device
          const earpiece = availableDevices.find(d =>
            d.label.toLowerCase().includes('earpiece') ||
            d.label.toLowerCase().includes('handset') ||
            d.label.toLowerCase().includes('phone') ||
            d.label.toLowerCase().includes('receiver')
          );

          // Find speaker device
          const speaker = availableDevices.find(d =>
            d.label.toLowerCase().includes('speaker') ||
            d.deviceId === 'default'
          ) || availableDevices[0];

          if (newUseSpeaker && speaker) {
            await audio.setSinkId(speaker.deviceId);
          } else if (!newUseSpeaker && earpiece) {
            await audio.setSinkId(earpiece.deviceId);
          } else if (!newUseSpeaker && availableDevices.length > 1) {
            // If no earpiece found, try the second device
            const altDevice = availableDevices.find(d => d.deviceId !== speaker?.deviceId);
            if (altDevice) {
              await audio.setSinkId(altDevice.deviceId);
            }
          }
        }
      } catch (err) {
        console.log('Audio output switch error:', err);
      }
    }
  }, [useSpeaker, availableDevices]);

  // On mobile/unsupported browsers, show an informational badge
  if (!isSupported) {
    return (
      <div
        className="audio-output-info"
        title="Use headphones for private listening. Audio routing is managed by your device."
      >
        <span className="audio-output-icon">🔊</span>
        <span className="audio-output-text">Device Audio</span>
      </div>
    );
  }

  return (
    <button
      onClick={toggleAudioOutput}
      className={`audio-output-btn ${useSpeaker ? 'speaker' : 'earpiece'}`}
      title={useSpeaker ? 'Switch to Earpiece' : 'Switch to Speaker'}
    >
      {useSpeaker ? '🔊 Speaker' : '📱 Earpiece'}
    </button>
  );
};

export const VolumeControl = () => {
  const [volume, setVolume] = useState(100);

  const handleVolumeChange = (e) => {
    const newVolume = parseInt(e.target.value);
    setVolume(newVolume);

    document.querySelectorAll('audio').forEach((audio) => {
      audio.volume = newVolume / 100;
    });
  };

  return (
    <div className="volume-control">
      <span>🔈</span>
      <input
        type="range"
        min="0"
        max="100"
        value={volume}
        onChange={handleVolumeChange}
        className="volume-slider"
      />
      <span>🔊</span>
    </div>
  );
};